// apps/vendor/src/lib/api/csrfFetch.ts
//
// Thin wrapper around `fetch` that auto-attaches the `X-XSRF-TOKEN` header on
// mutating verbs (POST/PUT/PATCH/DELETE) from the readable `XSRF-TOKEN`
// cookie set by the server's `@fastify/csrf-protection` plugin (double-submit
// pattern). Mirrors the merchant-v3 portal's `apiClient` CSRF
// behaviour (apps/merchant-v3/src/lib/api/client.ts) but keeps the call shape
// identical to raw `fetch()` so existing `features/*/api.ts` files migrate
// with a one-token replacement.
//
// Why a vendor-local helper instead of `@printway/shared`'s `apiClient()`?
//   - The shared client uses `openapi-fetch` with typed paths, which would
//     require a much larger diff to migrate every vendor api.ts.
//   - This helper is a drop-in for raw `fetch()` — same signature, same
//     return type — keeping the migration minimal-risk and reviewable.
//   - Also auto-injects `credentials: 'include'` (cookie auth) so callers
//     don't have to remember it.

import { handleUnauthorized } from '@printway/shared/api';

const MUTATING_VERBS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// dbg-vendor-csrf-deadlock — single-flight refresh to recover an expired
// XSRF cookie (15m TTL). After /auth/refresh the API rotates BOTH the
// access JWT and the XSRF cookie, so a 403 AUTH_CSRF_MISSING is silently
// recoverable for the duration of the long-lived refresh-token cookie.
let refreshInFlight: Promise<boolean> | null = null;

function readXsrfCookieRaw(): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie;
  if (typeof raw !== 'string') return null;
  for (const part of raw.split('; ')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq) === 'XSRF-TOKEN') {
      try {
        return decodeURIComponent(part.slice(eq + 1));
      } catch {
        return part.slice(eq + 1);
      }
    }
  }
  return null;
}

async function refreshSession(apiBase: string): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(apiBase + '/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

// Sandbox audit 2026-05-15 — vendor-v3 nginx vhost has NO `/api/` proxy
// rule, so `csrfFetch('/api/v1/...')` resolved to vendor-v3 origin +
// returned the SPA index.html with 200 + text/html. `useReports` (and
// every sibling feature hook) then threw on JSON parse. Mirror the
// merchant-v3 apiClient's behaviour: prefix relative `/api/...` paths
// with `VITE_API_BASE_URL` baked at build time. Absolute URLs untouched.
const ENV_API_BASE = (
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
).replace(/\/$/, '');

function withApiBase(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input !== 'string') return input;
  if (!input.startsWith('/api/')) return input;
  if (!ENV_API_BASE) return input;
  return ENV_API_BASE + input;
}

function readXsrfCookie(): string | null {
  return readXsrfCookieRaw();
}

/**
 * Drop-in replacement for `fetch()` that:
 *   1. Always sends cookies (`credentials: 'include'`).
 *   2. Auto-attaches `X-XSRF-TOKEN` from the `XSRF-TOKEN` cookie on
 *      POST/PUT/PATCH/DELETE.
 *
 * Caller-supplied headers + credentials take precedence (caller is trusted).
 */
export async function csrfFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const isMutating = MUTATING_VERBS.has(method);

  async function attempt(): Promise<Response> {
    const merged = new Headers(init.headers ?? {});
    if (isMutating && !merged.has('X-XSRF-TOKEN')) {
      const token = readXsrfCookie();
      if (token) merged.set('X-XSRF-TOKEN', token);
    }
    return fetch(withApiBase(input), {
      credentials: 'include',
      ...init,
      headers: merged,
    });
  }

  let res = await attempt();

  // dbg-vendor-csrf-deadlock — when an idle session's XSRF cookie expires
  // (15m TTL) before its refresh token (7d), every mutating call fails with
  // 403 AUTH_CSRF_MISSING / AUTH_CSRF_MISMATCH. Recover transparently by
  // calling /auth/refresh once (now exempt from CSRF check), then retrying
  // the original request with the rotated cookie.
  if (isMutating && res.status === 403) {
    const body = await res
      .clone()
      .json()
      .catch(() => null);
    const errCode =
      body && typeof body === 'object' && body !== null
        ? ((body as { error?: { code?: unknown } }).error?.code as string | undefined)
        : undefined;
    if (errCode === 'AUTH_CSRF_MISSING' || errCode === 'AUTH_CSRF_MISMATCH') {
      const refreshed = await refreshSession(ENV_API_BASE);
      if (refreshed) {
        res = await attempt();
      }
    }
  }

  // dbg-20260716 — on 401, try a single /auth/refresh + retry BEFORE logging
  // out (parity with merchant-v3's apiClient). A transient 401 (e.g. access
  // token briefly unverifiable right after an API restart, or a lost
  // rotate-race across the 3 shared-cookie localhost SPAs) then self-recovers
  // instead of dumping the vendor to /login. Only hard-logout if refresh
  // itself fails or the retried request still 401s.
  if (res.status === 401) {
    const refreshed = await refreshSession(ENV_API_BASE);
    if (refreshed) {
      res = await attempt();
    }
    if (res.status === 401) {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : (input as Request).url;
      handleUnauthorized(url);
    }
  }

  // UAT #2658 — global maintenance gate. The BE returns 503
  // MAINTENANCE_MODE_ACTIVE for all non-exempt endpoints while the platform is
  // under maintenance. Drop the whole SPA to the full-screen /maintenance page
  // instead of leaving a clickable shell whose every request silently 503s.
  if (res.status === 503) {
    const body = await res
      .clone()
      .json()
      .catch(() => null);
    const err =
      body && typeof body === 'object' && body !== null
        ? (body as { error?: { code?: unknown; user_message?: unknown; message?: unknown } }).error
        : undefined;
    if (err?.code === 'MAINTENANCE_MODE_ACTIVE') {
      const msg =
        typeof err.user_message === 'string'
          ? err.user_message
          : typeof err.message === 'string'
            ? err.message
            : undefined;
      redirectToMaintenance(msg);
    }
  }
  return res;
}

/**
 * Stash the operator message + hard-redirect to /maintenance (unless already
 * there, to avoid a loop). Hard nav keeps this dependency-free from routing.
 */
const MAINTENANCE_MESSAGE_KEY = 'pw_maintenance_msg';
function redirectToMaintenance(message: string | undefined): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/maintenance') return;
  try {
    if (message && message.trim().length > 0) {
      window.sessionStorage.setItem(MAINTENANCE_MESSAGE_KEY, message);
    }
  } catch {
    /* sessionStorage unavailable — still redirect */
  }
  window.location.assign('/maintenance');
}
